import './styles/stylesheet.css';
import Navbar from './components/Navbar'
import { BrowserRouter, Routes, Route, Outlet, Navigate, useLocation } from 'react-router-dom';
import Timetable from './pages/Timetable';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import 'dayjs/locale/de';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import dayjs from 'dayjs';
import Login from './pages/Login';
import Admin from './pages/Admin';
import Account from './pages/Account';
import Contact from './pages/Contact';
// import Results from './pages/Results';
import { AuthProvider, useAuth } from './provider/auth';
import { ToastContainer } from 'react-toastify';
// import Tournaments from './pages/Tournaments';
import ResetPassword from "./pages/ResetPassword";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Box, CircularProgress, Typography } from '@mui/material';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Europe/Berlin");

// Definiere dein eigenes Theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#d32f2f', // Ein kräftiges Rot für Akzente (z.B. Buttons, Filter)
    },
    success: {
      main: '#2e7d32',
    },
    warning: {
      main: '#ed6c02',
    },
    info: {
      main: '#0288d1',
    },
  },
  typography: {
    fontFamily: '"Poppins", sans-serif',
    h5: {
      fontWeight: 500,
    },
    h4: {
      fontWeight: 500,
    },
    h6: {
      fontWeight: 500,
    },
    button: {
      textTransform: 'none', // Modernere Buttons ohne Auto-Großschreibung
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12, // Modernere, abgerundete Ecken
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '8px 20px',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0px 4px 20px rgba(0,0,0,0.05)', // Sanfterer, moderner Schatten
          borderRadius: 12,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
          },
        },
      },
    },
  },
});

const LoadingSpinner = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <CircularProgress />
  </Box>
);

const OfflineOverlay = () => {
  const { isOffline } = useAuth();
  if (!isOffline) return null;
  return (
    <Box sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      bgcolor: 'rgba(255,255,255,0.7)',
      backdropFilter: 'blur(8px)',
      zIndex: 10000,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      textAlign: 'center',
      p: 3
    }}>
      <CircularProgress color="error" sx={{ mb: 3 }} size={60} />
      <Typography variant="h4" color="error" gutterBottom sx={{ fontWeight: 600 }}>
        Verbindung unterbrochen
      </Typography>
      <Typography variant="h6" color="textSecondary">
        Versuche die Verbindung zum Server wiederherzustellen...
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
        In wenigen Sekunden erfolgt ein automatisches Logout, falls der Server nicht erreichbar bleibt.
      </Typography>
    </Box>
  );
};

function App() {
  return (
    
    <BrowserRouter>
      <ToastContainer />
      <AuthProvider>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
          <ThemeProvider theme={theme}>
          <OfflineOverlay />
          <Routes>
            <Route element={<LayoutWithoutNav />}>
              <Route path="/login" element={<Login />} />
              <Route path="/reset-password" element={<ResetPassword />}/>
              <Route path="/set-password" element={<ResetPassword />}/>
            </Route>
            <Route element={<LayoutWithNav />}>
              <Route path="/" element={<ProtectedRoute><Timetable /></ProtectedRoute>} />
              <Route path="/contact" element={<ProtectedRoute><Contact /></ProtectedRoute>} />
              <Route path="/admin/*" element={<AdminRoute requiredRole={2}><Admin /></AdminRoute>} />
              <Route path="/account" element={<ProtectedRoute ><Account /></ProtectedRoute>} />
              {/* Globaler Catch-all für unbekannte URLs -> Startseite */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
          </ThemeProvider>
        </LocalizationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

const LayoutWithNav = () => {
  const { loading } = useAuth();

  if (loading) return <LoadingSpinner />;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Navbar />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          width: '100%',
          maxWidth: '1600px',
          mx: 'auto',
          mt: 0
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

const LayoutWithoutNav = () => <Outlet />;

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingSpinner />;
  
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  return children;
};


const AdminRoute = ({ children, requiredRole }) => {
  const { user, loading } = useAuth();

  if (loading) return <LoadingSpinner />;

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRole !== undefined && user.role < requiredRole) {
    return <Navigate to="/" replace />;
  }

  return children;
};


export const API_BASE_URL = process.env.REACT_APP_API_URL;

export default App;
